import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  ConversationStatus,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { useEffect, useState } from "react";
import { getMergeGroupForConversation } from "../merge-suggestion/utils";

export interface UseMergeSuggestionsResult {
  suggestedConversationsToMerge: MinimalConversation[] | undefined;
  mergeSuggestionDismissed: boolean;
  mergeSuggestionFlowState: "dialog" | "merge" | undefined;
  setMergeSuggestionFlowState: (state: "dialog" | "merge" | undefined) => void;
  resetMergeSuggestionFlow: () => void;
}

export function useMergeSuggestions(
  activeConversation: MinimalConversation,
): UseMergeSuggestionsResult {
  const client = useRequiredContext(RedoMerchantClientContext);

  const [mergeSuggestionDismissed, setMergeSuggestionDismissed] =
    useState<boolean>(true);
  const [suggestedConversationsToMerge, setSuggestedConversationsToMerge] =
    useState<MinimalConversation[] | undefined>();
  const [mergeSuggestionFlowState, setMergeSuggestionFlowState] = useState<
    "dialog" | "merge" | undefined
  >();

  const resetMergeSuggestionFlow = useHandler(() => {
    setMergeSuggestionFlowState(undefined);
    setSuggestedConversationsToMerge(undefined);
    setMergeSuggestionDismissed(true);
  });

  /** When a new conversation is loaded or conversation status changes, fetch merge suggestions */
  useEffect(() => {
    const primaryEmail = getPrimaryCustomerEmail(activeConversation.customer);

    // Don't check for merge suggestions if there's no email or the conversation is closed
    if (
      activeConversation.status === ConversationStatus.CLOSED.toLowerCase() ||
      !primaryEmail
    ) {
      return;
    }

    const abortController = new AbortController();
    void (async () => {
      try {
        const { data: mergeGroup } = await getMergeGroupForConversation({
          client,
          conversationId: activeConversation._id,
          signal: abortController.signal,
        });

        setMergeSuggestionDismissed(mergeGroup.dismissed);

        const suggestedConversations = mergeGroup.conversations.filter(
          ({ _id }) => _id !== activeConversation._id,
        );

        if (suggestedConversations.length === 0) {
          setSuggestedConversationsToMerge(undefined);
          return;
        }

        setSuggestedConversationsToMerge(suggestedConversations);

        if (!mergeGroup.dismissed) {
          setMergeSuggestionFlowState("dialog");
        }
      } catch (e: any) {
        if (e.name === "CanceledError") {
          return;
        }
        throw e;
      }
    })();

    return () => abortController.abort();
  }, [
    activeConversation._id,
    activeConversation.status,
    activeConversation.customer,
    client,
  ]);

  return {
    suggestedConversationsToMerge,
    mergeSuggestionDismissed,
    mergeSuggestionFlowState,
    setMergeSuggestionFlowState,
    resetMergeSuggestionFlow,
  };
}
